import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { log } from "@/lib/log";

export interface FlowUser {
  sub: string;
  email: string | null;
  name: string | null;
  /** 3-letter Keycloak preferred_username (unique LogicLine employee key). */
  username: string | null;
  picture: string | null;
  roles: string[];
  accessToken: string | null;
}

const DEV_USER_STORAGE_KEY = "flow.devUserSub";

function isDevBypass(): boolean {
  return import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
}

function devUserSub(): string {
  const stored = typeof window !== "undefined" ? localStorage.getItem(DEV_USER_STORAGE_KEY) : null;
  return stored ?? import.meta.env.VITE_DEV_USER_SUB ?? "dev-user-1";
}

let _userManager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (_userManager) return _userManager;
  if (isDevBypass()) {
    throw new Error("UserManager unavailable when dev auth bypass is enabled");
  }
  const origin = window.location.origin;
  _userManager = new UserManager({
    authority: import.meta.env.VITE_KEYCLOAK_ISSUER,
    client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: origin,
    response_type: "code",
    scope: "openid profile email",
    automaticSilentRenew: true,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
  return _userManager;
}

export function devModeEnabled(): boolean {
  return isDevBypass();
}

export function getDevUserSub(): string {
  return devUserSub();
}

export function setDevUserSub(sub: string): void {
  localStorage.setItem(DEV_USER_STORAGE_KEY, sub);
}

export function userFromOidc(u: User | null): FlowUser | null {
  if (!u) return null;
  const out = new Set<string>();
  // Realm roles like flow-admin usually live in the ACCESS token (what the
  // backend validates), not the ID-token profile — so read both and union, and
  // include client (resource_access) roles too. Mirrors the backend _extract_roles.
  collectRoles(u.profile, out);
  collectRoles(decodeJwt(u.access_token), out);
  return {
    sub: u.profile.sub,
    email: (u.profile.email as string | undefined) ?? null,
    name: (u.profile.name as string | undefined) ?? null,
    username: (u.profile.preferred_username as string | undefined) ?? null,
    picture: (u.profile.picture as string | undefined) ?? null,
    roles: [...out],
    accessToken: u.access_token ?? null,
  };
}

function collectRoles(claims: Record<string, unknown> | null, out: Set<string>): void {
  if (!claims) return;
  const realm = (claims as { realm_access?: { roles?: string[] } }).realm_access;
  for (const r of realm?.roles ?? []) out.add(r);
  const resource = (claims as { resource_access?: Record<string, { roles?: string[] }> })
    .resource_access;
  for (const client of Object.values(resource ?? {})) {
    for (const r of client.roles ?? []) out.add(r);
  }
}

// Decode a JWT payload (base64url) without verifying — the FE only needs the
// role claims for UI gating; the backend independently verifies the token.
function decodeJwt(token: string | undefined): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function devUser(): FlowUser {
  const sub = devUserSub();
  return {
    sub,
    email: `${sub}@dev.local`,
    name: `Dev User ${sub.slice(0, 8)}`,
    username: sub.slice(0, 3).toLowerCase(),
    picture: null,
    roles: ["flow-admin", "flow-member"],
    accessToken: null,
  };
}

export async function loginRedirect(returnTo?: string | null): Promise<void> {
  const target = returnTo && returnTo.startsWith("/") ? returnTo : "/";
  if (isDevBypass()) {
    window.location.href = target;
    return;
  }
  // `state` round-trips through Keycloak unchanged and lands back at our
  // /auth/callback as user.state.
  await getUserManager().signinRedirect({ state: { returnTo: target } });
}

export async function logoutRedirect(): Promise<void> {
  if (isDevBypass()) {
    localStorage.removeItem(DEV_USER_STORAGE_KEY);
    window.location.href = "/login";
    return;
  }
  try {
    await getUserManager().signoutRedirect();
  } catch (err) {
    log.error("Signout redirect failed", err);
  }
}

export async function completeLoginCallback(): Promise<string | null> {
  if (isDevBypass()) return null;
  const user = await getUserManager().signinRedirectCallback();
  const state = user?.state as { returnTo?: string } | undefined;
  const candidate = state?.returnTo;
  return candidate && candidate.startsWith("/") ? candidate : null;
}

export async function trySilentRenew(): Promise<FlowUser | null> {
  if (isDevBypass()) return devUser();
  try {
    const u = await getUserManager().signinSilent();
    return userFromOidc(u);
  } catch (err) {
    log.warn("Silent renew failed", err);
    return null;
  }
}

export async function getCurrentToken(): Promise<string | null> {
  if (isDevBypass()) return null;
  const u = await getUserManager().getUser();
  return u?.access_token ?? null;
}
